import { useMemo, useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Loading from '@/components/general/Loading';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import interviewsService, { PostPublicInterview } from '@/services/interviews';
import { FetchApiIds, CandidateInterviewPageStages } from '@/types';
import { Paths } from '@/types/routes';
import CandidatePreScreeningInterviewComponent from './CandidatePreScreeningInterview.component';

type CandidateAnswerType = {
  label: string;
  id: string;
  recordingBlob: any;
  url: string;
};

export default function CandidatePreScreeningInterview(): JSX.Element {
  const toast = useToastContext();
  const { interviewId, candidateId } = useParams();

  const [interviewState, setInterviewState] = useState(CandidateInterviewPageStages.WELCOME);
  const [alertState, setAlertState] = useState(false);
  const [canAnswerInterview, setCanAnswerInterview] = useState(true);
  const [candidateAnswers, setCandidateAnswers] = useState<CandidateAnswerType[]>([]);

  const interviewMutation = useApiSubmit<void, PostPublicInterview>(
    interviewsService.postCandidatePublicInterview
  );

  const interviewResponse = useApiFetch(
    [FetchApiIds.PUBLIC_CANDIDATE_INTERVIEW, interviewId, candidateId],
    async () =>
      interviewsService.getCandidatePublicInterview({
        interviewId: interviewId as string,
        candidateId: candidateId as string,
      }),
    {
      enabled: !!interviewId && !!candidateId,
    }
  );

  useEffect(() => {
    const { status } = interviewResponse.data?.person ?? {};
    const languageCheckStatusPrefix = 'LANGUAGE_CHECK';
    if (status && status !== `${languageCheckStatusPrefix}_SENT`) {
      setInterviewState(CandidateInterviewPageStages.ENDING);
    }
    if (status) {
      setCanAnswerInterview(!!status?.startsWith(languageCheckStatusPrefix));
    }
  }, [interviewResponse.data?.person]);

  const { questions, candidateName } = useMemo(() => {
    const formatQuestion = (question): any => ({ label: question.questionText, id: question.id });

    const { firstName, lastName } = interviewResponse?.data?.person ?? {};
    const formattedQuestions = interviewResponse?.data?.questions.map(formatQuestion) ?? [];

    return {
      candidateName: `${firstName} ${lastName}`,
      questions: formattedQuestions,
    };
  }, [interviewResponse?.data?.person, interviewResponse?.data?.questions]);

  const onChangeInterviewState = (type: CandidateInterviewPageStages): void => {
    setInterviewState(type);
  };

  const onSubmitAnswers = (answers: any): void => {
    setCandidateAnswers(answers);
    setAlertState((prev) => !prev);
  };

  const onConfirmSubmitAnswers = async (): Promise<void> => {
    try {
      const payload = new FormData();
      [...candidateAnswers].forEach(({ recordingBlob, id }) => {
        payload.append(id, recordingBlob);
      });
      payload.append('interviewId', interviewId ?? '');
      await interviewMutation.mutateAsync({ payload, interviewId: interviewId ?? '' });
      toast.showSuccessFeedback('The answers have been submited.');
      setAlertState(false);
      onChangeInterviewState(CandidateInterviewPageStages.ENDING);
    } catch {
      toast.showErrorFeedback('There was a problem to submit the answers, try again later.');
    }
  };

  const onCancelSubmitting = (): void => {
    setAlertState(false);
    setCandidateAnswers([]);
  };

  const { isLoading, isError } = interviewResponse;

  if (isLoading) {
    return <Loading fullPage />;
  }

  if (isError || !canAnswerInterview) {
    return <Navigate to={Paths.NOT_FOUND} />;
  }

  return (
    <CandidatePreScreeningInterviewComponent
      questions={questions}
      isSubmitting={interviewMutation.isLoading}
      candidateName={candidateName}
      onChangeInterviewState={onChangeInterviewState}
      onSubmitAnswers={onSubmitAnswers}
      interviewState={interviewState}
      alertState={alertState}
      onConfirmSubmitAnswers={onConfirmSubmitAnswers}
      onCancelSubmitting={onCancelSubmitting}
    />
  );
}
